import type { RoutePricingBuildTypeRate } from '@/lib/commerce';
import { currencyFractionDigits } from './currency';

export interface RoutePricingBuildTypeRateFormValue {
  rowId: string;
  buildType: string;
  currency: string;
  pricePerMeter: string;
  minimumBuildCost: string;
}

export interface RoutePricingRateValidationIssue {
  field: string;
  message: string;
  rowId: string;
}

export interface ParsedRoutePricingRates {
  rates: RoutePricingBuildTypeRate[];
  validationIssues: RoutePricingRateValidationIssue[];
}

function canonicalDecimal(value: string) {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(value.trim());
  if (!match) return null;
  const whole = match[1].replace(/^0+(?=\d)/, '');
  const fraction = (match[2] || '').replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : whole;
}

function fractionDigits(value: string) {
  return value.trim().split('.')[1]?.length || 0;
}

function isSupportedCurrency(currency: string) {
  if (!/^[A-Z]{3}$/.test(currency)) return false;
  try {
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(0);
    return true;
  } catch {
    return false;
  }
}

function issue(row: RoutePricingBuildTypeRateFormValue, field: string, message: string) {
  return { rowId: row.rowId, field: `${row.rowId}.${field}`, message };
}

export function hydrateRoutePricingBuildTypeRates(
  rates?: RoutePricingBuildTypeRate[] | null,
): RoutePricingBuildTypeRateFormValue[] {
  return (rates || []).map((rate, index) => ({
    rowId: `route-rate-${index + 1}`,
    buildType: rate.build_type,
    currency: rate.currency,
    pricePerMeter: rate.price_per_meter,
    minimumBuildCost: rate.minimum_build_cost || '',
  }));
}

export function parseRoutePricingBuildTypeRates(
  rows: RoutePricingBuildTypeRateFormValue[],
  options: { label?: string; expectedCurrency?: string | null } = {},
): ParsedRoutePricingRates {
  const label = options.label || 'Build-type rate';
  const expectedCurrency = options.expectedCurrency?.trim().toUpperCase() || '';
  const seenBuildTypes = new Set<string>();
  const validationIssues: RoutePricingRateValidationIssue[] = [];
  const rates: RoutePricingBuildTypeRate[] = [];

  rows.forEach(row => {
    const buildType = row.buildType.trim().toLowerCase();
    const currency = row.currency.trim().toUpperCase();
    const pricePerMeter = canonicalDecimal(row.pricePerMeter);
    const minimumBuildCost = row.minimumBuildCost.trim()
      ? canonicalDecimal(row.minimumBuildCost)
      : undefined;

    if (!buildType) validationIssues.push(issue(row, 'buildType', `${label} build type is required.`));
    if (buildType && seenBuildTypes.has(buildType)) {
      validationIssues.push(issue(row, 'buildType', `${label} build type ${buildType} is duplicated.`));
    }
    seenBuildTypes.add(buildType);
    if (!isSupportedCurrency(currency)) {
      validationIssues.push(issue(row, 'currency', `${label} currency must be a valid uppercase ISO-4217 code.`));
    } else if (expectedCurrency && currency !== expectedCurrency) {
      validationIssues.push(issue(row, 'currency', `${label} currency must match ${expectedCurrency}.`));
    }
    if (!pricePerMeter || pricePerMeter === '0' || fractionDigits(row.pricePerMeter) > 4) {
      validationIssues.push(issue(row, 'pricePerMeter', `${label} price per meter must be greater than 0 with no more than 4 decimal places.`));
    }
    if (row.minimumBuildCost.trim()) {
      const digits = isSupportedCurrency(currency) ? currencyFractionDigits(currency) : 2;
      if (minimumBuildCost == null || fractionDigits(row.minimumBuildCost) > digits) {
        validationIssues.push(issue(
          row,
          'minimumBuildCost',
          `${label} minimum build cost must be non-negative with no more than ${digits} decimal places for ${currency || 'the selected currency'}.`,
        ));
      }
    }

    if (!validationIssues.some(validationIssue => validationIssue.rowId === row.rowId)) {
      rates.push({
        build_type: buildType,
        currency,
        price_per_meter: pricePerMeter as string,
        ...(minimumBuildCost !== undefined ? { minimum_build_cost: minimumBuildCost } : {}),
      });
    }
  });

  return { rates: rates.sort((left, right) => left.build_type.localeCompare(right.build_type)), validationIssues };
}

export function buildAccessPointRatePayload(
  rows: RoutePricingBuildTypeRateFormValue[],
  expectedCurrency?: string | null,
) {
  const result = parseRoutePricingBuildTypeRates(rows, {
    label: 'Access-point fallback rate',
    expectedCurrency,
  });
  return { ...result, payload: { default_build_type_rates: result.rates } };
}

export function buildServiceabilityZoneRatePayload(
  rows: RoutePricingBuildTypeRateFormValue[],
  expectedCurrency?: string | null,
) {
  const result = parseRoutePricingBuildTypeRates(rows, {
    label: 'Market rate',
    expectedCurrency,
  });
  return { ...result, payload: { build_type_rates: result.rates } };
}

export function buildFlatPriceBandPreview(options: {
  distanceMeters: string;
  pricePerMeter: string;
  minimumBuildCost?: string | null;
  currency: string;
}): { calculatedCost: number; appliedCost: number; label: string } | null {
  const distance = Number(options.distanceMeters);
  const rate = Number(options.pricePerMeter);
  const minimum = options.minimumBuildCost?.trim() ? Number(options.minimumBuildCost) : 0;
  if (![distance, rate, minimum].every(Number.isFinite) || distance < 0 || rate <= 0 || minimum < 0) return null;
  const calculatedCost = distance * rate;
  const appliedCost = Math.max(calculatedCost, minimum);
  const currency = options.currency.trim().toUpperCase() || 'USD';
  const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency });
  return {
    calculatedCost,
    appliedCost,
    label: `${options.distanceMeters.trim()} m × ${formatter.format(rate)}/m = ${formatter.format(calculatedCost)}; quoted build cost ${formatter.format(appliedCost)}${minimum > calculatedCost ? ' minimum applied' : ''}.`,
  };
}

export function buildMinimumBuildCostBandLabel(options: {
  pricePerMeter: string;
  minimumBuildCost: string;
  currency: string;
}): string | null {
  const rate = Number(options.pricePerMeter);
  const minimum = Number(options.minimumBuildCost);
  if (!Number.isFinite(rate) || rate <= 0 || !Number.isFinite(minimum) || minimum < 0) return null;
  if (minimum === 0) return 'No minimum floor; distance pricing starts at zero.';
  const currency = options.currency.trim().toUpperCase() || 'USD';
  const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency });
  const threshold = minimum / rate;
  return `The ${formatter.format(minimum)} minimum applies through approximately ${new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(threshold)} m at ${formatter.format(rate)}/m.`;
}
